import React, { useState, useEffect } from 'react';
import { FormControl, Link, Typography } from '@material-ui/core';
import { ModalComponent, PasslyMaskedInput, PasslyButton, PasslyTextField } from '@app/components';
import { digitRegex } from '@app/helpers';
import { IPolicyAuth, IRadiusClient, AgentConfigTypeEnum } from '@app/models';
import { AddRadiusClient } from '../dialogs/addRadiusClient.dialog';
import { FormMode } from '@app/helpers/enums';
import { useTypedTranslation } from '@app/hooks';


interface IProps {
    updateConfigCallback: Function;
    updateProtectedConfigCallback: Function;
    formValidCallback: Function;
    clientId: any;
    isEditView: boolean;
    editRadiusPort: number;
    editClients: IRadiusClient[];
    authPolicies: IPolicyAuth[];
}

const RadiusConfig: React.SFC<IProps> = ({ updateConfigCallback, updateProtectedConfigCallback, isEditView, authPolicies, editClients, editRadiusPort, formValidCallback }) => {
    const [clients, setClients] = useState<IRadiusClient[]>(isEditView ? editClients : []);
    const [openAddModal, setOpenAddModal] = useState<boolean>(false);
    const [openEditModal, setOpenEditModal] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [radiusPort, setRadiusPort] = useState<number | string>(isEditView ? editRadiusPort : 1812);

    const handleModalCloseCallback = () => {
        setOpenAddModal(!openAddModal);
    };

    const handleModalCloseEditCallback = () => {
        setOpenEditModal(!openEditModal);
    };
    const { translation } = useTypedTranslation();

    useEffect(() => {
        updateProtectedConfigCallback(clients, AgentConfigTypeEnum.radiusConfig);
    }, [clients]);

    useEffect(() => {
        updateConfigCallback(radiusPort);
        if (radiusPort !== undefined && radiusPort !== 0 && radiusPort !== '') {
            formValidCallback(true);
        }
        else {
            formValidCallback(false);
        }
    }, [radiusPort]);

    const removeClient = (index: number) => {
        let newClients = clients.filter((item, i) => i !== index);
        setClients(newClients);
    };

    const addClientDialog = () => {
        setOpenAddModal(!openAddModal);
    };

    const editClient = (index: number) => {
        setSelectedIndex(index);
        setOpenEditModal(!openEditModal);
    };

    const handleAddClient = (client: IRadiusClient, index?: number) => {
        if (index !== undefined) {
            let clientsCopy: IRadiusClient[] = [...clients];
            clientsCopy[index] = client;
            setClients(clientsCopy);
        }
        else {
            setClients(clients.concat(client));
        }
    };

    const onPortChange = (e: any) => {
        setRadiusPort(e.target.value);
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('ADD_RADIUS_CLIENT')}
                form={<AddRadiusClient
                    authPolicies={authPolicies}
                    formMode={FormMode.Add}
                    toggleModal={handleModalCloseCallback}
                    onAddClient={handleAddClient}
                    clients={clients} />
                }
                modalSwitcherCallback={handleModalCloseCallback}
                localModalSwitcher={openAddModal}
            />
            <ModalComponent
                headerTitle={translation('EDIT_RADIUS_CLIENTS')}
                form={<AddRadiusClient
                    index={selectedIndex}
                    authPolicies={authPolicies}
                    formMode={FormMode.Edit}
                    toggleModal={handleModalCloseEditCallback}
                    onAddClient={handleAddClient}
                    existingConfig={clients[selectedIndex]}
                    clients={clients} />
                }
                modalSwitcherCallback={handleModalCloseEditCallback}
                localModalSwitcher={openEditModal}
            />
            <div className='rowcontainer'>
                <div className='configpanel-left configpanel-left--radius-configuration'>
                    <div className='container'>
                        <FormControl>
                            <PasslyTextField
                                id='radiusport'
                                label={'APP_RADIUS_PORT_LABEL'}
                                value={radiusPort}
                                onChange={onPortChange}
                                fullWidth
                                InputProps={{
                                    inputComponent: PasslyMaskedInput,
                                    inputProps: {
                                        maskSize: 7,
                                        maskRegex: digitRegex
                                    },
                                }}
                            />
                        </FormControl>
                        <Typography variant='h3'>{translation('RADIUS_CLIENTS')}</Typography>
                        {clients.length > 0 ? <>
                            <div className='configpanel-left--radius-configuration-row'>
                                <div>{translation('RADIUS_CLIENT_NAME_LABEL')}</div>
                                <div>{translation('RADIUS_CLIENT_IP_LABEL')}</div>
                                <div>{translation('RADIUS_CLIENT_POLICY_LABEL')}</div>
                                <div></div>
                                <div></div>
                            </div>
                            <div className='configpanel-left--radius-configuration-column'>{
                                clients.map((client: IRadiusClient, index: number) => (
                                    <div key={index} className='configpanel-left--radius-configuration-rowcontainer'>
                                        <div>{client.name}</div>
                                        <div>{client.ip}</div>
                                        <div>{client.policy.label}</div>
                                        <div id={`editClient${index}`}>
                                            <Link onClick={() => editClient(index)}>{translation('EDIT')}</Link>
                                        </div>
                                        <div id={`removeClient${index}`}>
                                            <Link onClick={() => removeClient(index)}>{translation('REMOVE')}</Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                            :
                            <span>{translation('EMPTY_RADIUS_CLIENT_LIST')}</span>
                        }
                    </div>
                </div>
                <div className='configpanel-right configpanel-right--radius-configuration'>
                    <div dangerouslySetInnerHTML={{ __html: translation('RADIUS_CONFIG_HELPER_HEADER') }} />
                    <PasslyButton
                        id='addRadiusClient'
                        variant='contained'
                        color='primary'
                        onClick={() => addClientDialog()}
                    >
                        {translation('ADD_RADIUS_CLIENT')}
                    </PasslyButton>
                </div>
            </div>
        </>
    );
};

export default RadiusConfig;
